import { useCallback } from 'react';
import {
  FilesGroupedByRefId,
  TransformedFile,
  transformFile,
} from './useReferenceFilesTypes';
import * as queries from 'query/study/Documents';

const _groupFilesByReferenceId = (
  files: queries.GetFilesWithDocumentsResponse
): FilesGroupedByRefId => {
  const groupedByRefId: FilesGroupedByRefId = new Map();

  files.references.forEach((r) => {
    if (groupedByRefId.has(r.review_reference_id)) {
      const existingFiles = groupedByRefId.get(r.review_reference_id) || [];
      groupedByRefId.set(
        r.review_reference_id,
        _sortPdfFilesByPrimary([...existingFiles, transformFile(r.document)])
      );
    } else {
      groupedByRefId.set(r.review_reference_id, [transformFile(r.document)]);
    }
  });

  return groupedByRefId;
};

const _sortPdfFilesByPrimary = (pdfFiles: TransformedFile[]) => {
  return [...pdfFiles].sort((a, b) => {
    if (a.isPrimary) return -1;
    if (b.isPrimary) return 1;
    return 0;
  });
};

type useFetchAndTransformFilesInApiReturn = {
  fetchFiles: (referenceIds: number[]) => Promise<FilesGroupedByRefId>;
  deleteFile: (file: TransformedFile) => Promise<void>;
  makeFilePrimary: (
    referenceUrl: string,
    fileId: string | number
  ) => Promise<void>;
  deleteDocumentUrl: (referenceUrl: string) => Promise<void>;
};

export const useFetchAndTransformFilesInApi = (
  reviewId?: string
): useFetchAndTransformFilesInApiReturn => {
  const fetchFiles = useCallback(
    async (referenceIds: number[]): Promise<FilesGroupedByRefId> => {
      if (!reviewId || referenceIds.length === 0) {
        return new Map();
      }

      const files = await queries.getFilesWithDocuments(reviewId, referenceIds);
      const groupedByRefId: FilesGroupedByRefId = _groupFilesByReferenceId(
        files
      );

      return groupedByRefId;
    },
    [reviewId]
  );

  const deleteFile = async (file: TransformedFile): Promise<void> => {
    await queries.deleteFile(Number(file.id));
  };

  const makeFilePrimary = async (
    referenceUrl: string,
    fileId: string | number
  ): Promise<void> => {
    await queries.makeFilePrimary(referenceUrl, fileId);
  };

  const deleteDocumentUrl = async (referenceUrl: string): Promise<void> => {
    await queries.unsetDocumentUrl(referenceUrl);
  };

  return { fetchFiles, deleteFile, makeFilePrimary, deleteDocumentUrl };
};
